import { useLogin, usePrivy } from '@privy-io/react-auth';
import { useQueryIpBlockStatus } from '@vertex-protocol/react-client';
import { useDialog } from 'client/modules/app/dialogs/hooks/useDialog';
import {
  PRIVY_CONNECT_WALLET_OPTIONS,
  PRIVY_EMBEDDED_LOGIN_OPTIONS,
} from 'client/modules/app/appData/getPrivyProviderConfig';
import {
  createContext,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
} from 'react';

type ConnectWalletAction<T extends Element = Element> = (
  event?: MouseEvent<T>,
) => void;

const ConnectWalletActionContext = createContext<
  ConnectWalletAction | undefined
>(undefined);

export function useConnectWalletAction<T extends Element = Element>() {
  return useContext(ConnectWalletActionContext) as
    | ConnectWalletAction<T>
    | undefined;
}

export function ConnectWalletActionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { login } = useLogin();
  const { authenticated, connectWallet: connectPrivyWallet } = usePrivy();
  const { data: ipBlockStatus } = useQueryIpBlockStatus();
  const { show } = useDialog();

  const connectWallet = useCallback<ConnectWalletAction>(() => {
    if (ipBlockStatus) {
      show({
        type: 'connect',
        params: {},
      });
      return;
    }

    if (authenticated) {
      connectPrivyWallet(PRIVY_CONNECT_WALLET_OPTIONS);
      return;
    }

    login(PRIVY_EMBEDDED_LOGIN_OPTIONS);
  }, [authenticated, connectPrivyWallet, ipBlockStatus, login, show]);

  return (
    <ConnectWalletActionContext.Provider value={connectWallet}>
      {children}
    </ConnectWalletActionContext.Provider>
  );
}
